'use client';

import { useState, useEffect, useRef } from 'react';
import { mergeChatMessages, type ChatMessage as Message } from '@/app/lib/chat';

type Step = 'closed' | 'form' | 'chat';


const STORAGE_KEY = 'ptc_chat';

function saveSession(sessionId: string, lastId: number) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId, lastId })); } catch {}
}
function clearSession() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

export default function HelpChat() {
  const [step, setStep] = useState<Step>('closed');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [firstMessage, setFirstMessage] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [followUp, setFollowUp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const lastIdRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Restore session from localStorage on mount + listen for contact form sessions
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { sessionId: sid, lastId } = JSON.parse(saved);
        if (sid) {
          setSessionId(sid);
          lastIdRef.current = lastId ?? 0;
          setStep('chat');
          // Fetch existing messages so the thread isn't empty on restore
          fetch(`/api/chat/${sid}/messages?after=0`)
            .then(r => r.json())
            .then(data => { if (data.messages?.length > 0) setMessages(data.messages); })
            .catch(() => {});
        }
      }
    } catch {}

    // When the contact form on the same page starts a session, adopt it
    const onChatStarted = (e: Event) => {
      const { sessionId: sid } = (e as CustomEvent).detail;
      if (!sid) return;
      setSessionId(sid);
      lastIdRef.current = 0;
      setMessages([]);
      setStep('chat');
      // Fetch the initial message
      fetch(`/api/chat/${sid}/messages?after=0`)
        .then(r => r.json())
        .then(data => { if (data.messages?.length > 0) setMessages(data.messages); })
        .catch(() => {});
    };
    window.addEventListener('ptc:chat-started', onChatStarted);
    return () => window.removeEventListener('ptc:chat-started', onChatStarted);
  }, []);

  // Scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for new messages while chat is open
  useEffect(() => {
    if (step !== 'chat' || !sessionId) return;

    const poll = async () => {
      try {
        const res = await fetch(`/api/chat/${sessionId}/messages?after=${lastIdRef.current}`);
        const data = await res.json();
        if (data.messages?.length > 0) {
          // Merge, don't append: the poll echoes back the user's own
          // messages, already rendered optimistically without an id.
          setMessages(prev => mergeChatMessages(prev, data.messages));
          const newLastId = data.messages[data.messages.length - 1].id;
          lastIdRef.current = newLastId;
          saveSession(sessionId, newLastId);
        }
      } catch {
        // Silently ignore poll errors
      }
    };

    const interval = setInterval(poll, 4000);
    return () => clearInterval(interval);
  }, [step, sessionId]);

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch('/api/chat/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message: firstMessage }),
      });
      const data = await res.json();
      if (data.sessionId) {
        lastIdRef.current = 0;
        saveSession(data.sessionId, 0);
        setSessionId(data.sessionId);
        setMessages([{ role: 'user', content: firstMessage }]);
        setStep('chat');
      } else {
        alert(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      alert('Could not send message. Please check your connection.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFollowUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!followUp.trim()) return;
    const text = followUp.trim();
    setFollowUp('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    try {
      await fetch(`/api/chat/${sessionId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text }),
      });
    } catch {
      // Optimistic update already shown
    }
  };

  const handleEndChat = () => {
    clearSession();
    setSessionId('');
    setMessages([]);
    lastIdRef.current = 0;
    setStep('closed');
  };

  const hasSupport = messages.some(m => m.role === 'support');

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">

      {/* Chat panel */}
      {step !== 'closed' && (
        <div className="w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
             style={{ maxHeight: '480px' }}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#0f172a] text-white">
            <div className="flex items-center gap-2">
              <span className="text-lg">💬</span>
              <span className="font-bold text-sm">
                {step === 'form' ? 'Chat with us' : 'Live Support'}
              </span>
            </div>
            <button
              onClick={() => setStep('closed')}
              className="text-slate-400 hover:text-white text-lg leading-none transition-colors"
              aria-label="Close chat"
            >✕</button>
          </div>

          {/* Form step */}
          {step === 'form' && (
            <form onSubmit={handleStart} className="flex flex-col gap-3 p-4 overflow-y-auto">
              <p className="text-sm text-slate-500">We usually reply within a few minutes.</p>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Your name"
                required
                className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-900 focus:ring-2 focus:ring-red-400 outline-none"
              />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Your email"
                required
                className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-900 focus:ring-2 focus:ring-red-400 outline-none"
              />
              <textarea
                value={firstMessage}
                onChange={e => setFirstMessage(e.target.value)}
                placeholder="How can we help?"
                required
                rows={3}
                className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-900 focus:ring-2 focus:ring-red-400 outline-none resize-none"
              />
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2.5 bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white font-bold rounded-xl text-sm transition-colors active:scale-95"
              >
                {submitting ? 'Sending...' : 'Send Message →'}
              </button>
            </form>
          )}

          {/* Chat step */}
          {step === 'chat' && (
            <>
              <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3" style={{ minHeight: 0 }}>
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm leading-snug ${
                      msg.role === 'user'
                        ? 'bg-red-700 text-white rounded-br-sm'
                        : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                    }`}>
                      {msg.content}
                    </div>
                  </div>
                ))}
                {!hasSupport && (
                  <div className="flex justify-start">
                    <div className="max-w-[75%] px-3 py-2 rounded-2xl text-sm text-slate-400 bg-slate-50 rounded-bl-sm italic">
                      We'll reply shortly...
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={handleFollowUp} className="flex gap-2 p-3 border-t border-slate-100">
                <input
                  type="text"
                  value={followUp}
                  onChange={e => setFollowUp(e.target.value)}
                  placeholder="Reply..."
                  className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-900 focus:ring-2 focus:ring-red-400 outline-none"
                />
                <button
                  type="submit"
                  disabled={!followUp.trim()}
                  className="px-3 py-2 bg-red-700 hover:bg-red-800 disabled:bg-slate-200 text-white font-bold rounded-xl text-sm transition-colors"
                >
                  →
                </button>
              </form>

              <div className="px-4 py-2 border-t border-slate-100 text-center">
                <button
                  onClick={handleEndChat}
                  className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                >
                  End chat
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setStep(step === 'closed' ? (sessionId ? 'chat' : 'form') : 'closed')}
        className="flex items-center gap-2 px-5 py-3 bg-red-700 hover:bg-red-800 text-white font-bold rounded-full shadow-lg hover:shadow-red-900/40 transition-all active:scale-95 text-sm"
        aria-label="Toggle help chat"
      >
        <span>{step === 'closed' ? '💬' : '✕'}</span>
        <span>{step === 'closed' ? 'Help' : 'Close'}</span>
      </button>
    </div>
  );
}
